import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ShoppingBagIcon, StarIcon } from 'lucide-react';
import { HERO_BENEFITS, IMAGES, Pkg } from '../data/content';
type Props = {
  selectedPackage: Pkg;
  onChangePackage: () => void;
  onBuyNow: () => void;
};
export function Hero({
  selectedPackage,
  onChangePackage,
  onBuyNow
}: Props) {
  return <section id="top" className="grid w-full overflow-hidden bg-texture pt-20 lg:min-h-screen lg:grid-cols-2 lg:pt-0" data-id="element-131" data-mp="4jvyliyrdmzy">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex items-center px-6 py-16 md:px-10 lg:px-16 lg:py-28 xl:px-24" data-id="element-132" data-mp="x9upzl0fdw7s">
        <div className="mx-auto w-full max-w-xl lg:mx-0" data-id="element-133" data-mp="nbyir4alzley">
          <h1 className="font-serif text-4xl font-medium leading-[1.03] tracking-tight text-forest sm:text-5xl lg:text-[4.25rem]" data-id="element-134" data-mp="3d1sayygxbltn">
            All Your Daily Nutrients.
            <br data-id="element-135" data-mp="3j8iv8twpu6s2" />
            <span className="italic text-sage" data-id="element-136" data-mp="3j7zhzqi0g6kd">The Natural Way.</span>
          </h1>

          <p className="mt-6 max-w-md text-base leading-relaxed text-forest/70" data-id="element-137" data-mp="3o7c243hhyzj9">
            A powerful blend of whole-food nutrition, plant botanicals and 21 essential vitamins &amp;
            minerals — one clean sachet for your everyday routine.
          </p>

          <div className="mt-7 hidden flex-wrap gap-x-5 gap-y-2.5 sm:flex" data-id="element-138" data-mp="3ebirwqr93i4n">
            {HERO_BENEFITS.map(benefit => <span key={benefit} className="flex items-center gap-1.5 text-sm font-medium text-forest/85" data-id="element-139" data-mp="358rdqj6zq89b">
                <CheckIcon className="h-4 w-4 text-sage" data-id="element-140" data-mp="2qk3m2o09mion" /> {benefit}
              </span>)}
          </div>

          <div className="mt-6 flex items-center gap-2" data-id="element-141" data-mp="2vi55xb5imm16">
            <span className="flex" data-id="element-142" data-mp="2vglld8cttotl">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-forest text-forest" data-id="element-143" data-mp="2hl9sz7m58afu" data-mpi={i} />)}
            </span>
            <span className="text-sm text-forest/70" data-id="element-144" data-mp="2lml8gkbazfab">
              <strong className="font-semibold text-forest" data-id="element-145" data-mp="s2tenov66frb">4.9/5</strong> from early customers
            </span>
          </div>

          <div className="mt-9 rounded-[1.75rem] border border-forest/10 bg-white/70 p-4 shadow-[0_18px_50px_rgba(6,46,36,0.08)] backdrop-blur-sm sm:p-5" data-id="element-146" data-mp="n98d5mudb3nd">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-147" data-mp="ddwzl7up42h7">
              <div data-id="element-148" data-mp="1b15prg5n5zpj">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sage" data-id="element-149" data-mp="1azkqxecanulv">Your daily ritual</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-150" data-mp="162m0b5rua5sf">
                  <span className="font-serif text-xl text-forest" data-id="element-151" data-mp="111eqz2a1syjn">{selectedPackage.name}</span>
                  {selectedPackage.popular && <span className="rounded-full bg-sage/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-forest" data-id="element-152" data-mp="3lsyun4p58wlp">
                      Most Popular
                    </span>}
                </div>
                <p className="mt-1 text-sm text-forest/65" data-id="element-153" data-mp="1xacdyyq6tbr4">
                  {selectedPackage.sachets} · <strong className="font-semibold text-forest" data-id="element-154" data-mp="21a4t5ua36g5g">{selectedPackage.price}</strong>
                  {selectedPackage.original && <span className="ml-1.5 text-forest/40 line-through" data-id="element-155" data-mp="268g1g0kqgilk">{selectedPackage.original}</span>}
                </p>
              </div>
              <button onClick={onChangePackage} className="inline-flex items-center justify-center gap-1.5 self-start rounded-full border border-forest/15 px-3.5 py-2 text-xs font-semibold text-forest transition-colors hover:border-forest hover:bg-forest hover:text-ivory sm:self-auto" data-id="element-156" data-mp="2fzss4ousp2q0">
                Change pack <ChevronDownIcon className="h-3.5 w-3.5" data-id="element-157" data-mp="2tyadwh15455a" />
              </button>
            </div>
            <button onClick={onBuyNow} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep" data-id="element-158" data-mp="unrdt69gy1xm">
              <ShoppingBagIcon className="h-4 w-4" data-id="element-159" data-mp="3vhp3brhz62nc" /> Buy Now
            </button>
            <p className="mt-3 text-center text-xs text-forest/55" data-id="element-160" data-mp="3vjrrs73hbnpy">Free shipping · 30-day satisfaction guarantee</p>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.15
    }} className="relative min-h-[480px] bg-forest-deep lg:min-h-full" data-id="element-161" data-mp="9bs7z6r3fzqv">
        <img src={IMAGES.hero} alt="A Medufit daily nutrition canister beside a glass of prepared green drink" className="absolute inset-0 h-full w-full object-cover" data-id="element-162" data-mp="3rftve45jgr0l" />
        <div className="absolute inset-x-0 bottom-0 bg-forest-deep/55 px-6 py-5 text-center md:px-10 lg:py-6" data-id="element-163" data-mp="2yz9ag1leslhy">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-ivory/75" data-id="element-164" data-mp="2u1hij7krkzdr">
            One sachet. One glass. One simple ritual.
          </p>
        </div>
      </motion.div>
    </section>;
}